import React, { useEffect, useState, useRef } from 'react';

import withCloseOnOutsideClick from '@/Helpers/withHandleClickOutside';
import { SearchIcon } from "@/Icons";

import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  FloatingFocusManager
} from '@floating-ui/react';

const Select = ({
  'item-count': itemCount,
  status,
  label,
  size,
  value,
  startIcon,
  endIcon,
  multiselect,
  placeholder,
  children,
  className,
  searchable,
  helperText,
  error,
  badgeVariant,
  onCloseOptions,
  disabled,
  variant,
  ...props
}) => {
  //HOC properties
  const { isOpen, setIsOpen, refNode, onChange, ...restOfProperties } = props;


  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(10), flip(), shift()],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start'
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  // Merge all the interactions into prop getters
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role
  ]);

  const [newChildren, setNewChildren] = useState(children);
  const [keyWord, setKeyWord] = useState('');
  const [cursor, setCursor] = useState(0);
  const [height, setHeight] = useState(48);
  const searchRef = useRef();
  const parentRef = useRef();

  const [width, setWidth] = useState(null);

  let childrenWithProps = [];

  const getSize = () => {
    if (size === 'small') {
      return 'select-small'
    } else if (size === 'medium') {
      return 'select-medium'
    } else if (size === 'large') {
      return 'select-large'
    } else if (size === 'full') {
      return 'select-full'
    } else if (size === 'xs') {
      return 'select-xs'
    }

    return 'select-normal'
  }

  const searchRefFocus = () => {
    searchRef.current.focus();
  };
  const executeScroll = () => {
    parentRef.current.scrollTo(0, height - 48)
  };

  let menuListHeight = 0;
  const childrenHeight = (size !== 'xs') ? 48 : 25;

  if (newChildren) {

    menuListHeight = newChildren.length > 7 ? 336 : (newChildren.length * childrenHeight);

    childrenWithProps = (
      React.Children.map(newChildren, (child, index) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            multiselect,
            index: index,
            selectedValue: value,
            handleClick: onChange,
            cursor: cursor,
            searchable: searchable,
            setCursor: setCursor,
            setKeyWord: setKeyWord,
            searchRefFocus: searchRefFocus,
            setHeight: setHeight,
          });
        }
        return child;
      })
    );
  }

  let activeValue = childrenWithProps.find(childrenWithProp => childrenWithProp.props.value === value);

  const handleSearch = (event) => {
    if (variant === 'menu') return;
    if (event.target.value && children) {
      const filtered = children.filter(child => {
        if (child && child.key) {
          return child.key.toLowerCase().includes(event.target.value.toLowerCase());
        }
        return false;
      });

      if (filtered.length > 0) {
        setNewChildren(filtered);
      } else {
        setNewChildren([]);
      }
    } else {
      setNewChildren(children);
    }
    setKeyWord(event.target.value);
    setCursor(0);
    setHeight(0);
    executeScroll();
  }

  const handleKeyDown = (e) => {
    if (cursor === 0) {
      setHeight(0);
    }

    if (!e) {
      return;
    }

    if (e.keyCode === 38 && cursor > 0) {
      setCursor(cursor - 1);
      setHeight(height - 48);
    } else if (e.keyCode === 40 && cursor < newChildren.length - 1) {
      setCursor(cursor + 1);
      setHeight(height + 48);
    }

    executeScroll();

    if (e.key?.toLowerCase()?.trim() === 'enter') {
      const enteredElement = newChildren[cursor];

      if (enteredElement) {
        let arrayOfValue = [];
        const enteredElementValue = enteredElement.props?.value;
        if (multiselect) {
          if (value.includes(enteredElementValue)) {
            arrayOfValue = [...value.filter(e => e !== enteredElementValue)];
          } else {
            arrayOfValue = [...value, enteredElementValue];
          }
        } else {
          arrayOfValue = enteredElementValue;
        }

        setHeight(0);
        setCursor(0);
        setKeyWord('');
        onChange({ target: { value: arrayOfValue } });
        parentRef.current.scrollTo(0, 0);
      }
    }
  };

  useEffect(() => {

    if (children && variant !== 'menu') {
      let newChildren = children.filter(child => child && child !== null);

      if (searchable) {
        const filtered = newChildren.filter(child => {
          if (child && child.key) {
            return child.key.toLowerCase().includes(keyWord?.toLowerCase());
          }
          return false;
        });
        setNewChildren(filtered);
      } else {
        setNewChildren(newChildren);
      }
    } else {
      setNewChildren([]);
    }
  }, [children, searchable, keyWord, variant]);



  useEffect(() => {
    if (onCloseOptions) {
      onCloseOptions(isOpen);
    }
  }, [isOpen, onCloseOptions]);

  const updateDimensions = () => {
    setWidth(window.innerWidth);
  };

  useEffect(() => {
    setWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', updateDimensions);

    return () => {
      window.removeEventListener('resize', updateDimensions);
    }
  }, []);

  useEffect(() => {
    if (isOpen && variant === 'default') {
      let indexOfCurrentSelectedOption = 0;

      for (const key in parentRef?.current?.children[0].children) {
        if (parentRef?.current?.children[0]?.children[key]?.classList?.contains('bg-purple-2')) {
          indexOfCurrentSelectedOption = key
        }
      }
      parentRef?.current?.children[0]?.children[indexOfCurrentSelectedOption]?.scrollIntoView();
    }

    
  }, [isOpen, variant, width]);

  const renderHelperText = () => {
    return (
      <div className={`textfield-helper ${(status !== 'default' ? status === 'validation' ? 'text-primary' : 'text-red' : '')}`}>
        {helperText}
      </div>
    );
  };

  const renderIcon = () => {
    if (endIcon) {
      return endIcon;
    }

    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className={isOpen ? 'rotate-180' : ''}>
        <path fillRule="evenodd" clipRule="evenodd" d="M6 9L12 15L18 9H6Z" fill="#231F20"/>
      </svg>
    );
  };

  return (
    <div ref={refNode} className={`select ${getSize()} ${className || ''}`}>
      {label && <div className="select-label">{label}</div>}

      <button ref={refs.setReference} {...getReferenceProps()} onClick={() => { setIsOpen(!isOpen); setCursor(0) }} className={`select-button outline-none`} disabled={disabled} {...restOfProperties}>
        <div className="select-button-content">
          <div className="flex items-center">
            {startIcon && startIcon}
            <span className={`select-button-label ${(startIcon ? 'ml-3' : '')} ${placeholder && !activeValue ? 'select-button-label-placeholder' : ''}`}>
              {
                activeValue && !multiselect &&
                activeValue.props.children
              }
              {
                multiselect &&
                placeholder
              }
              {
                !multiselect && !activeValue &&
                placeholder
              }
            </span>
          </div>
          <div className="flex items-center justify-between">
            {/* {multiselect && value.length > 0 && <BfBadge variant={badgeVariant ? badgeVariant : ''} text={value.length.toString()} />} */}
            {/* {itemCount && children.length > 0 && <BfBadge color="bg-primary" text={children.length.toString()} />} */}
            {renderIcon()}
          </div>
        </div>
      </button>
      
      {
        isOpen && variant !== 'menu' && !multiselect &&
        (
          <FloatingFocusManager context={context} modal={false}>
          <div className="select-single"
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          >
            {
              searchable && (
                <div className={`textfield`} style={{ borderBottom: '1px solid #a2aeb6' }}>
                  <div className={`textfield-group-search`}>
                    <div className={`textfield-group-icon`}>
                      <span className="flex items-center">
                        <SearchIcon />
                      </span>
                    </div>
                    <input value={keyWord} className={`textfield-group-input`} placeholder="Search" autoFocus={isOpen} onChange={(event) => handleSearch(event)} onKeyDown={(event) => handleKeyDown(event)} ref={searchRef} />
                  </div>
                </div>
              )
            }
            <div ref={parentRef} className={`select-menu ${(size === 'xs' ? 'select-menu-xs' : '')} ${searchable ? 'rounded-t-none' : ''}`} style={{ height: menuListHeight + 'px'}} onClick={() => setIsOpen(false)}>
              <ul>
                {
                  childrenWithProps && childrenWithProps.length > 0 ? childrenWithProps :
                    <div className="no-data">No data</div>
                }
              </ul>
              {/* {childrenWithProps && childrenWithProps.length > 1 && (
                <div className="select-footer"></div>
              )} */}
            </div>
          </div>
          </FloatingFocusManager>
        )
      }
      
      {(helperText && renderHelperText())}

    </div>
  );
};

export default withCloseOnOutsideClick(Select);
